import { useState } from 'react';
import axios from 'axios';
import { Upload, Check, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function InvoiceDispatchUpload({ items, customers, onCreated }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select an invoice PDF'); return; }
    setUploading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/transactions/dispatch/upload-invoice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });
      setPreview(response.data);
      setLines(response.data.items.map(item => ({
        ...item,
        include: item.in_master,
        final_part_id: item.matched_part_id || '',
        final_qty: item.quantity || 0
      })));
      setInvoiceNo(response.data.invoice_no || '');
      setInvoiceDate(response.data.invoice_date || new Date().toISOString().split('T')[0]);
      setCustomerId(response.data.matched_customer?.customer_id || '');
      toast.success(`Parsed ${response.data.total_items} line item(s) from invoice`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse invoice PDF');
    } finally { setUploading(false); }
  };

  const updateLine = (idx, patch) => setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const includedCount = lines.filter(l => l.include && l.final_part_id && parseInt(l.final_qty) > 0).length;

  const handleConfirm = async () => {
    const toCreate = lines.filter(l => l.include && l.final_part_id && parseInt(l.final_qty) > 0);
    if (toCreate.length === 0) { toast.error('No verified line items selected'); return; }
    if (!invoiceNo) { toast.error('Invoice number is required'); return; }
    if (!invoiceDate) { toast.error('Invoice date is required'); return; }
    if (!customerId) { toast.error('Please select the customer'); return; }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/transactions/dispatch/confirm-invoice`, {
        dispatches: toCreate.map(l => ({
          date: new Date(invoiceDate).toISOString(),
          part_id: l.final_part_id,
          customer: customerId,
          qty: parseInt(l.final_qty),
          invoice: invoiceNo
        }))
      });
      toast.success(response.data.message);
      setPreview(null); setLines([]); setFile(null);
      onCreated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispatches');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="invoice-upload-section">
      <div className="bg-white border border-border shadow-sm p-6">
        <h2 className="text-2xl font-bold uppercase mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>UPLOAD INVOICE PDF</h2>
        <div className="flex gap-3">
          <Input
            data-testid="invoice-pdf-input"
            type="file"
            accept=".pdf"
            onChange={(e) => setFile(e.target.files[0])}
            className="rounded-sm"
          />
          <Button
            data-testid="parse-invoice-button"
            onClick={handleUpload}
            disabled={uploading || !file}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium whitespace-nowrap"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'PARSING...' : 'PARSE INVOICE'}
          </Button>
        </div>
        {uploading && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
            <p className="text-sm text-blue-700">AI is reading your invoice and extracting dispatch data. This may take 15-30 seconds...</p>
          </div>
        )}
      </div>

      {preview && (
        <div className="bg-white border border-border shadow-sm">
          <div className="p-4 border-b border-border bg-amber-50/60 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-semibold text-amber-900">Verify the extracted details below before creating dispatch entries. Nothing is saved until you confirm.</span>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-border">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">Invoice No</label>
              <Input data-testid="verify-invoice-no" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} className="rounded-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">Dispatch Date</label>
              <Input data-testid="verify-invoice-date" type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="rounded-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5 block">
                Customer {preview.customer_name && <span className="normal-case font-normal text-gray-500">(PDF: {preview.customer_name})</span>}
              </label>
              <SearchableSelect
                testId="verify-customer-select"
                options={customers.map(c => ({ value: c.customer_id, label: c.customer_name, searchStr: `${c.customer_id} ${c.customer_name}` }))}
                value={customerId}
                onValueChange={setCustomerId}
                placeholder="Select customer..."
                searchPlaceholder="Type to search customers..."
              />
              {preview.matched_customer && customerId === preview.matched_customer.customer_id && (
                <p className="text-xs text-emerald-600 mt-1">Auto-matched: {preview.matched_customer.customer_name} ({Math.round(preview.matched_customer.score * 100)}%)</p>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-center bg-gray-50/50 uppercase text-xs tracking-wider font-medium text-muted-foreground w-12">Inc.</th>
                  <th className="h-10 px-4 text-left bg-gray-50/50 uppercase text-xs tracking-wider font-medium text-muted-foreground">Status</th>
                  <th className="h-10 px-4 text-left bg-gray-50/50 uppercase text-xs tracking-wider font-medium text-muted-foreground">Invoice Part ID</th>
                  <th className="h-10 px-4 text-left bg-gray-50/50 uppercase text-xs tracking-wider font-medium text-muted-foreground">Description</th>
                  <th className="h-10 px-4 text-left bg-gray-50/50 uppercase text-xs tracking-wider font-medium text-muted-foreground w-64">Dispatch As (Part)</th>
                  <th className="h-10 px-4 text-right bg-gray-50/50 uppercase text-xs tracking-wider font-medium text-muted-foreground w-32">Qty (pcs)</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, idx) => (
                  <tr key={idx} className={`border-b ${!line.in_master ? 'bg-red-50/30' : line.match_type === 'fuzzy' ? 'bg-amber-50/30' : ''}`} data-testid={`invoice-line-${idx}`}>
                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        checked={line.include}
                        onChange={() => updateLine(idx, { include: !line.include })}
                        className="rounded border-gray-300"
                        data-testid={`invoice-line-include-${idx}`}
                      />
                    </td>
                    <td className="p-3">
                      {line.in_master ? (
                        line.match_type === 'exact' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><Check className="w-3 h-3" /> Exact</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700"><AlertTriangle className="w-3 h-3" /> Fuzzy {Math.round(line.match_score * 100)}%</span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-700"><X className="w-3 h-3" /> Not Found</span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">{line.part_id}</td>
                    <td className="p-3 text-xs text-text-muted">{line.description}</td>
                    <td className="p-3">
                      <SearchableSelect
                        testId={`invoice-line-part-${idx}`}
                        options={items.map(i => ({ value: i.part_id, label: `${i.part_id} - ${i.part_name}`, searchStr: `${i.part_id} ${i.part_name}` }))}
                        value={line.final_part_id}
                        onValueChange={(v) => updateLine(idx, { final_part_id: v, include: true })}
                        placeholder="Map to part..."
                        searchPlaceholder="Type to search parts..."
                      />
                    </td>
                    <td className="p-3">
                      <Input
                        data-testid={`invoice-line-qty-${idx}`}
                        type="number"
                        min="1"
                        value={line.final_qty}
                        onChange={(e) => updateLine(idx, { final_qty: e.target.value })}
                        className="rounded-none h-8 text-right font-mono"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-border flex items-center justify-between">
            <span className="text-xs text-text-muted">{includedCount} of {lines.length} line(s) will be dispatched to the selected customer under invoice {invoiceNo || '—'}</span>
            <Button
              data-testid="confirm-invoice-dispatch-button"
              onClick={handleConfirm}
              disabled={saving || includedCount === 0}
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm font-medium"
            >
              <Check className="w-4 h-4 mr-2" />
              {saving ? 'CREATING...' : `CONFIRM & CREATE ${includedCount} DISPATCH${includedCount === 1 ? '' : 'ES'}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
